/**
 * AnomalyResolver — formulaire inline de résolution d'une anomalie.
 *
 *  - Textarea optionnelle `resolution_note`
 *  - Submit → PATCH /v1/anomalies/:id/resolve
 *  - Toast succès/erreur
 *  - onClose() callback pour replier le form (gestion par le parent)
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useResolveAnomaly } from "../../../lib/queries/anomalies";
import { toast } from "../../../lib/toast";

export type AnomalyResolverProps = {
  anomalyId: string;
  submissionId: string;
  onClose: () => void;
};

export function AnomalyResolver(props: AnomalyResolverProps) {
  const { anomalyId, submissionId, onClose } = props;
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [note, setNote] = useState("");
  const resolve = useResolveAnomaly();

  const onSubmit = async () => {
    try {
      await resolve.mutateAsync({
        anomalyId,
        submissionId,
        resolution_note: note.trim() ? note.trim() : null,
      });
      toast.success(t("submissions.detail.anomalies.resolve.success"));
      onClose();
    } catch (e) {
      const message =
        e instanceof ApiException
          ? getApiErrorMessage(e, lang) || t("errors.generic")
          : e instanceof Error
            ? e.message
            : t("errors.generic");
      toast.error(message);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-[color:var(--brand-bg)] p-3">
      <label
        htmlFor={`anomaly-note-${anomalyId}`}
        className="block text-xs font-medium text-slate-700"
      >
        {t("submissions.detail.anomalies.resolve.note_label")}
      </label>
      <textarea
        id={`anomaly-note-${anomalyId}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder={t("submissions.detail.anomalies.resolve.note_placeholder")}
        className="mt-1 w-full rounded-md border border-slate-200 bg-white p-2 text-sm"
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={resolve.isPending}
          className="btn-primary text-sm"
        >
          {resolve.isPending
            ? t("submissions.detail.anomalies.resolve.saving")
            : t("submissions.detail.anomalies.resolve.confirm")}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={resolve.isPending}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          {t("submissions.detail.anomalies.resolve.cancel")}
        </button>
      </div>
    </div>
  );
}
